import { useEffect, useState } from "react";
import { Link } from "react-router-dom";

import {
  getDispositionReport,
  uploadCertificateOfDestruction,
  getBusinessUnits,
  getDepartments,
  getLocations,
  getRetentionLabels,
  type DispositionReportFilter,
  type DispositionReportRow,
  type BusinessUnit,
  type Department,
  type RecordLocation,
  type RetentionLabel,
} from "@/api/physicalRecordsApi";

function getDateTimeSuffix() {
  const now = new Date();
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${now.getFullYear()}${pad(now.getMonth() + 1)}${pad(now.getDate())}_${pad(now.getHours())}${pad(now.getMinutes())}${pad(now.getSeconds())}`;
}

export default function AdminPhysicalRecordsDisposition() {
  const [filter, setFilter] = useState<DispositionReportFilter>({ eligibleOnly: true });
  const [rows, setRows] = useState<DispositionReportRow[]>([]);
  const [businessUnits, setBusinessUnits] = useState<BusinessUnit[]>([]);
  const [departments, setDepartments] = useState<Department[]>([]);
  const [locations, setLocations] = useState<RecordLocation[]>([]);
  const [retentionLabels, setRetentionLabels] = useState<RetentionLabel[]>([]);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [certFiles, setCertFiles] = useState<Record<number, File | null>>({});
  const [destructionOrder, setDestructionOrder] = useState<Record<number, string>>({});

  async function loadLookups() {
    const [bu, d, l, r] = await Promise.all([getBusinessUnits(), getDepartments(), getLocations(), getRetentionLabels()]);
    setBusinessUnits(bu);
    setDepartments(d);
    setLocations(l);
    setRetentionLabels(r);
  }

  async function runReport() {
    setLoading(true);
    setErr(null);
    try {
      const data = await getDispositionReport(filter);
      setRows(data);
    } catch (e: any) {
      setErr(e?.response?.data?.error ?? e?.message ?? "Failed to load disposition report.");
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    loadLookups();
    runReport();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function onUploadCertificate(boxId: number) {
    const file = certFiles[boxId];
    if (!file) {
      setErr("Choose a Certificate of Destruction file to upload first.");
      return;
    }
    try {
      setErr(null);
      await uploadCertificateOfDestruction(file, boxId, destructionOrder[boxId]);
      await runReport();
    } catch (e: any) {
      setErr(e?.response?.data?.error ?? e?.message ?? "Failed to upload certificate.");
    }
  }

  function exportExcel() {
    if (rows.length === 0) return;
    const tableRows = rows
      .map(
        (r) =>
          `<tr><td>${r.boxNumber}</td><td>${r.boxName}</td><td>${r.offsiteId ?? ""}</td><td>${r.comment ?? ""}</td>` +
          `<td>${r.approver ?? ""}</td><td>${r.approvedDate ?? ""}</td><td>${r.locationName}</td><td>${r.retentionLabelName}</td>` +
          `<td>${r.businessUnitCode}</td><td>${r.departmentName}</td><td>${r.contentStartDate ?? ""}</td>` +
          `<td>${r.contentEndDate ?? ""}</td><td>${r.calculatedEligibleDate ?? ""}</td><td>${r.status}</td><td>${r.blockingReason ?? ""}</td></tr>`
      )
      .join("");

    const html = [
      "<html><head><meta charset=\"utf-8\"></head><body>",
      "<table border=\"1\">",
      "<thead><tr><th>Box Number</th><th>Box Name</th><th>Offsite Id</th><th>Review Comment</th><th>Approver</th>" +
        "<th>Approval Date</th><th>Location</th><th>Retention Label</th><th>Business Unit</th><th>Department</th>" +
        "<th>Content Start Date</th><th>Content End Date</th><th>Calculated Date</th><th>Approval</th></tr></thead>",
      `<tbody>${tableRows}</tbody></table></body></html>`,
    ].join("");

    const blob = new Blob([html], { type: "application/vnd.ms-excel;charset=utf-8;" });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.setAttribute("download", `box-disposition-report_${getDateTimeSuffix()}.xls`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  }

  return (
    <div className="container py-4">
      <div className="mb-3">
        <h1 className="h4 mb-1">Box Disposition Report</h1>
        <div className="text-muted">
          Eligibility = Content End Date + Retention Label duration. Director approval workflow is out of scope —
          Status/Approver/Comment columns are schema-only placeholders for now.
        </div>
      </div>

      {err && <div className="alert alert-danger">{err}</div>}

      <div className="card mb-3">
        <div className="card-body">
          <div className="row g-2 align-items-end">
            <div className="col-2 form-check">
              <input
                className="form-check-input"
                type="checkbox"
                id="eligibleOnly"
                checked={!!filter.eligibleOnly}
                onChange={(e) => setFilter({ ...filter, eligibleOnly: e.target.checked })}
              />
              <label className="form-check-label" htmlFor="eligibleOnly">
                Eligible only
              </label>
            </div>
            <div className="col-2">
              <label className="form-label">Business Unit</label>
              <select className="form-select" value={filter.businessUnitId ?? ""} onChange={(e) => setFilter({ ...filter, businessUnitId: e.target.value ? Number(e.target.value) : undefined })}>
                <option value="">Any</option>
                {businessUnits.map((b) => (
                  <option key={b.businessUnitId} value={b.businessUnitId}>
                    {b.code}
                  </option>
                ))}
              </select>
            </div>
            <div className="col-2">
              <label className="form-label">Department</label>
              <select className="form-select" value={filter.departmentId ?? ""} onChange={(e) => setFilter({ ...filter, departmentId: e.target.value ? Number(e.target.value) : undefined })}>
                <option value="">Any</option>
                {departments.map((d) => (
                  <option key={d.departmentId} value={d.departmentId}>
                    {d.name}
                  </option>
                ))}
              </select>
            </div>
            <div className="col-2">
              <label className="form-label">Location</label>
              <select className="form-select" value={filter.locationId ?? ""} onChange={(e) => setFilter({ ...filter, locationId: e.target.value ? Number(e.target.value) : undefined })}>
                <option value="">Any</option>
                {locations.map((l) => (
                  <option key={l.locationId} value={l.locationId}>
                    {l.name}
                  </option>
                ))}
              </select>
            </div>
            <div className="col-2">
              <label className="form-label">Retention Label</label>
              <select className="form-select" value={filter.retentionLabelId ?? ""} onChange={(e) => setFilter({ ...filter, retentionLabelId: e.target.value ? Number(e.target.value) : undefined })}>
                <option value="">Any</option>
                {retentionLabels.map((r) => (
                  <option key={r.retentionLabelId} value={r.retentionLabelId}>
                    {r.name}
                  </option>
                ))}
              </select>
            </div>
            <div className="col-2">
              <button className="btn btn-primary w-100" disabled={loading} onClick={runReport}>
                Run Report
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className="d-flex justify-content-between align-items-center mb-2">
        <div>{rows.length} box(es)</div>
        <button className="btn btn-outline-primary" disabled={rows.length === 0} onClick={exportExcel}>
          Export to Excel
        </button>
      </div>

      <table className="table table-sm">
        <thead>
          <tr>
            <th>Box</th>
            <th>Business Unit / Department</th>
            <th>Location</th>
            <th>Retention Label</th>
            <th>Content End Date</th>
            <th>Calculated Eligible Date</th>
            <th>Status</th>
            <th>Certificate of Destruction</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => (
            <tr key={r.dispositionSnapshotId}>
              <td>
                <Link to="/physical-records">
                  {r.boxNumber} — {r.boxName}
                </Link>
              </td>
              <td>
                {r.businessUnitCode} / {r.departmentName}
              </td>
              <td>{r.locationName}</td>
              <td>{r.retentionLabelName}</td>
              <td>{r.contentEndDate ?? "—"}</td>
              <td>{r.calculatedEligibleDate ?? "—"}</td>
              <td>{r.status}{r.blockingReason && <div className="small text-warning">{r.blockingReason}</div>}</td>
              <td>
                {r.certificateFilePath ? (
                  <span className="text-success">Uploaded {r.destructionOrderNumber ? `(Order ${r.destructionOrderNumber})` : ""}</span>
                ) : (
                  <div className="d-flex gap-1">
                    <input
                      type="text"
                      className="form-control form-control-sm"
                      placeholder="Order #"
                      style={{ width: 90 }}
                      value={destructionOrder[r.boxId] ?? ""}
                      onChange={(e) => setDestructionOrder({ ...destructionOrder, [r.boxId]: e.target.value })}
                    />
                    <input
                      type="file"
                      className="form-control form-control-sm"
                      onChange={(e) => setCertFiles({ ...certFiles, [r.boxId]: e.target.files?.[0] ?? null })}
                    />
                    <button className="btn btn-sm btn-outline-primary text-nowrap" onClick={() => onUploadCertificate(r.boxId)}>
                      Upload
                    </button>
                  </div>
                )}
              </td>
            </tr>
          ))}
          {rows.length === 0 && (
            <tr>
              <td colSpan={8} className="text-muted text-center">
                No boxes match the current filters.
              </td>
            </tr>
          )}
        </tbody>
      </table>
    </div>
  );
}
